import React, { useState } from "react";
import classNames from "classnames";
import {
    SortableContext,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Counterparty } from "@common/types/counterparty";
import { NormalizedDescriptionInfo } from "@common/types/normalizedDescription";
import {
    TransactionRuleGroup,
    isRuleHiddenForGlFirst,
} from "@common/types/transactionRule";
import { RuleScopeContext } from "@common/helpers/ruleOverrides";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { useTypedFlags } from "@hooks/useTypedFlags";
import styles from "../RulesTable.module.scss";
import { isRuleVisibleForFeatures } from "../utils";
import { EmptyState } from "./EmptyState";
import { RuleSortableRow } from "./RuleSortableRow";
import { useRuleGroupWarnings } from "./utils";

/**
 * RulesGroupBody renders all rules for a group, or its empty state.
 */
interface RulesGroupBodyProps {
    readonly group: TransactionRuleGroup;
    readonly overId: string | null;
    readonly groupListeners: React.HTMLAttributes<HTMLElement>;
    readonly groupAttributes: React.HTMLAttributes<HTMLElement>;
    readonly isGroupDragged: boolean;
    readonly counterparties: Counterparty[];
    readonly normalizedDescriptions: NormalizedDescriptionInfo[];
    readonly transactionCounts: Record<string, number>;
    readonly matchingCounts: Record<string, number>;
    readonly collapsedGroupId?: string | null;
    readonly priority?: number;
    readonly scopeContext: RuleScopeContext;
    readonly routeRuleId: string | null;
    readonly onRouteRuleClose: () => void;
}

export const RulesGroupBody: React.FC<RulesGroupBodyProps> = ({
    group,
    overId,
    groupListeners,
    groupAttributes,
    isGroupDragged,
    counterparties,
    normalizedDescriptions,
    transactionCounts,
    matchingCounts,
    collapsedGroupId,
    priority,
    scopeContext,
    routeRuleId,
    onRouteRuleClose,
}) => {
    const [isLocalCollapsed, setIsLocalCollapsed] = useState(false);

    // Group is collapsed if it's the one being dragged (collapsedGroupId === group.id)
    const isCollapsed = collapsedGroupId === group.id || isLocalCollapsed;

    const warningsByRuleId = useRuleGroupWarnings(group, scopeContext);

    const { enabled: isGlFirst } = useIsGlFirst();
    const { schedules: isSchedulesEnabled } = useTypedFlags();

    const visibleRules = (group.rules ?? []).filter(
        (rule) =>
            !isRuleHiddenForGlFirst(rule, { isGlFirst }) &&
            isRuleVisibleForFeatures(rule, { isSchedulesEnabled }),
    );

    return (
        <SortableContext
            key={group.id + "-rules"}
            items={visibleRules.map((r) => r.id)}
            strategy={verticalListSortingStrategy}
        >
            {visibleRules.length === 0 ? (
                <tr
                    className={styles.rowHover}
                    style={{ position: "relative" }}
                >
                    <EmptyState
                        group={group}
                        groupListeners={groupListeners}
                        groupAttributes={groupAttributes}
                        priority={priority}
                    />
                    {overId === group.id && isGroupDragged && (
                        <>
                            <div
                                className={classNames(
                                    styles.insertionIndicator,
                                    styles.top,
                                    styles.withDot,
                                )}
                            />
                            <div
                                className={classNames(
                                    styles.insertionIndicatorDot,
                                    styles.top,
                                )}
                            />
                        </>
                    )}
                </tr>
            ) : (
                visibleRules.map((rule, ruleIndex) => {
                    const warnings = warningsByRuleId.get(rule.id);
                    return (
                        <RuleSortableRow
                            key={rule.id}
                            rule={rule}
                            ruleIndex={ruleIndex}
                            group={group}
                            overId={overId}
                            groupListeners={groupListeners}
                            groupAttributes={groupAttributes}
                            isGroupDragged={isGroupDragged}
                            counterparties={counterparties}
                            normalizedDescriptions={normalizedDescriptions}
                            transactionCounts={transactionCounts}
                            matchingCounts={matchingCounts}
                            isCollapsed={isCollapsed}
                            toggleCollapse={() =>
                                setIsLocalCollapsed(!isLocalCollapsed)
                            }
                            priority={priority}
                            isUnreachable={warnings?.isUnreachable ?? false}
                            isDuplicate={warnings?.isDuplicate ?? false}
                            routeRuleId={routeRuleId}
                            onRouteRuleClose={onRouteRuleClose}
                        />
                    );
                })
            )}
        </SortableContext>
    );
};
